'use client'

import { LanguageISO6391 } from 'ai-constants'
import React, {
  createContext,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
} from 'react'
import { I18nextProvider } from 'react-i18next'
import {
  activateLanguage,
  getCurrentLanguage,
  isActiveLanguageOwner,
  LanguageOwner,
  releaseLanguage,
} from '../active-language'
import { changeI11nLanguage, getI11nInstance } from '../client-i18n'

export interface LanguageContextProps {
  lang: LanguageISO6391
}

type LanguageContextValue = LanguageContextProps & {
  owner: LanguageOwner
}

export const LanguageContext = createContext<LanguageContextValue | undefined>(
  undefined,
)

export const useLanguage = (): LanguageContextProps =>
  useContext(LanguageContext) ?? { lang: getCurrentLanguage() }

export type LanguageProviderScope = 'document' | 'subtree'

const controlsDocumentLanguageByScope = {
  document: () => true,
  subtree: (hasParentProvider: boolean) => !hasParentProvider,
} satisfies Record<
  LanguageProviderScope,
  (hasParentProvider: boolean) => boolean
>

export const LanguageProvider = ({
  children,
  lang,
  scope = 'document',
}: {
  children: React.ReactNode
  scope?: LanguageProviderScope
} & LanguageContextProps) => {
  const i18n = getI11nInstance()
  const parentContext = useContext(LanguageContext)
  const ownerId = useId()
  const parentOwnerRef = useRef(parentContext?.owner)
  parentOwnerRef.current = parentContext?.owner
  const depth = (parentContext?.owner.depth ?? 0) + 1
  const controlsDocumentLanguage = controlsDocumentLanguageByScope[scope](
    !!parentContext,
  )

  const owner = useMemo(
    () => ({ id: ownerId, depth, lang }),
    [depth, lang, ownerId],
  )
  const contextOwner =
    controlsDocumentLanguage || !parentContext ? owner : parentContext.owner
  const contextValue = useMemo(
    () => ({ lang, owner: contextOwner }),
    [contextOwner, lang],
  )

  if (controlsDocumentLanguage) activateLanguage(owner)

  useEffect(() => {
    if (!controlsDocumentLanguage) return
    if (!isActiveLanguageOwner(ownerId)) return

    document.documentElement.lang = lang
    void changeI11nLanguage(lang)
  }, [controlsDocumentLanguage, lang, ownerId])

  useEffect(
    () => () => releaseLanguage(ownerId, parentOwnerRef.current),
    [ownerId],
  )

  return (
    <I18nextProvider i18n={i18n}>
      <LanguageContext.Provider value={contextValue}>
        {children}
      </LanguageContext.Provider>
    </I18nextProvider>
  )
}
